/**
 * Button - Core Component (Enhanced Wrapper)
 *
 * This component wraps shadcn/ui Button with significant project-specific enhancements.
 * It adds loading states, icon support, and design token integration.
 *
 * Enhancements over shadcn/ui:
 * - Loading state with spinner animation (loading prop)
 * - Start and end icon slots (startIcon, endIcon props)
 * - Full width option (fullWidth prop)
 * - Design token classes for variant-specific styling
 * - Proper aria attributes for accessibility (aria-disabled, aria-busy)
 *
 * Design Tokens Used:
 * - rounded-button-{size} - Border radius per size
 * - px-button-{size}-x, py-button-{size}-y - Padding per size
 * - h-button-lg-height - Height for lg size
 * - Typography tokens (Primary variant only): text-button-{size}, font-button-{size}
 * - Outline variant: bg-button-outline-background, border-button-outline
 *
 * @see {@link file://./../ui/button.tsx} for the underlying implementation
 */
import { Loader2 } from 'lucide-react';
import { forwardRef, type ComponentProps, type ReactNode } from 'react';
import { Button as ShadcnButton } from '@/app/_components/ui/button';
import { cn } from '@/app/_lib/utils';

export type ButtonVariant =
  | 'default'
  | 'secondary'
  | 'destructive'
  | 'outline'
  | 'ghost'
  | 'link';
export type ButtonSize = 'sm' | 'default' | 'lg' | 'icon';

export interface ButtonProps extends Omit<ComponentProps<'button'>, 'size'> {
  /**
   * The visual style variant of the button
   * @default 'default'
   */
  variant?: ButtonVariant;
  /**
   * The size of the button
   * @default 'default'
   */
  size?: ButtonSize;
  /**
   * Whether the button should render as a child component (for use with Link, etc.)
   * @default false
   */
  asChild?: boolean;
  /**
   * Whether the button should take full width
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Loading state - shows a spinner and disables the button
   * @default false
   */
  loading?: boolean;
  /**
   * Icon to show before the button text
   */
  startIcon?: ReactNode;
  /**
   * Icon to show after the button text
   */
  endIcon?: ReactNode;
}

const getCustomVariantStyles = (variant: ButtonVariant) => {
  switch (variant) {
    case 'outline':
      return 'bg-button-outline-background text-foreground border-button-outline';
    case 'ghost':
      return 'text-foreground';
    default:
      return '';
  }
};

const getCustomSizeStyles = (size: ButtonSize) => {
  switch (size) {
    case 'sm':
      return 'rounded-button-sm px-button-sm-x py-button-sm-y';
    case 'default':
      return 'rounded-button-default px-button-default-x py-button-default-y';
    case 'lg':
      return 'h-button-lg-height rounded-button-lg px-button-lg-x py-button-lg-y';
    case 'icon':
      return 'rounded-button-icon px-button-icon-x py-button-icon-y';
    default:
      return 'rounded-button-default px-button-default-x py-button-default-y';
  }
};

// Typography styles based on variant and size
// Primary (default): Custom bold typography tokens that vary by size
// Mobile values as default, desktop values from lg breakpoint
// All other variants: text-sm font-medium (consistent across sizes)
const getTypographyStyles = (variant: ButtonVariant, size: ButtonSize) => {
  // Primary variant uses custom typography tokens per size
  if (variant === 'default') {
    switch (size) {
      case 'sm':
        return [
          'text-button-sm font-button-sm tracking-button-sm font-button-typeface-sm',
          'lg:text-button-sm-desktop lg:font-button-sm-desktop lg:tracking-button-sm-desktop lg:font-button-typeface-sm-desktop',
        ].join(' ');
      case 'lg':
        return [
          'text-button-lg font-button-lg tracking-button-lg font-button-typeface-lg',
          'lg:text-button-lg-desktop lg:font-button-lg-desktop lg:tracking-button-lg-desktop lg:font-button-typeface-lg-desktop',
        ].join(' ');
      case 'icon':
        return [
          'text-button-icon font-button-icon tracking-button-icon font-button-typeface-icon',
          'lg:text-button-icon-desktop lg:font-button-icon-desktop lg:tracking-button-icon-desktop lg:font-button-typeface-icon-desktop',
        ].join(' ');
      // 'default' size + defensive fallback
      default:
        return [
          'text-button-default font-button-default tracking-button-default font-button-typeface-default',
          'lg:text-button-default-desktop lg:font-button-default-desktop lg:tracking-button-default-desktop lg:font-button-typeface-default-desktop',
        ].join(' ');
    }
  }

  // All other variants: text-sm (14px), font-medium is already in shadcn base
  return 'text-sm';
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'default',
      size = 'default',
      className,
      fullWidth = false,
      loading = false,
      startIcon,
      endIcon,
      children,
      disabled,
      asChild,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // When asChild is true, Slot expects exactly one child
    // We can't render loading, startIcon, or endIcon in this case
    if (asChild) {
      return (
        <ShadcnButton
          ref={ref}
          variant={variant}
          size={size}
          asChild={true}
          className={cn(
            getCustomVariantStyles(variant),
            getCustomSizeStyles(size),
            getTypographyStyles(variant, size),
            fullWidth && 'w-full',
            // Subtle press feedback (link has no surface to press)
            variant !== 'link' && 'active:scale-[0.97]',
            '[&>svg]:size-4',
            className
          )}
          {...props}
        >
          {children}
        </ShadcnButton>
      );
    }

    // Normal button rendering with all features
    return (
      <ShadcnButton
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          getCustomVariantStyles(variant),
          getCustomSizeStyles(size),
          getTypographyStyles(variant, size),
          fullWidth && 'w-full',
          'cursor-pointer',
          loading && 'cursor-wait',
          // Subtle press feedback (link has no surface to press)
          variant !== 'link' && 'active:scale-[0.97]',
          '[&>svg]:size-4',
          className
        )}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-busy={loading}
        {...props}
      >
        {loading && (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        )}
        {!loading && startIcon && (
          <span
            className="flex shrink-0 items-center [&>svg]:size-4"
            aria-hidden="true"
          >
            {startIcon}
          </span>
        )}
        {children}
        {!loading && endIcon && (
          <span
            className="flex shrink-0 items-center [&>svg]:size-4"
            aria-hidden="true"
          >
            {endIcon}
          </span>
        )}
      </ShadcnButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
